import { format, type Locale } from 'date-fns';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle,
  Clock3,
  Link2,
  RefreshCw,
  UserPlus,
} from 'lucide-react-native';
import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { HStack, Sheet, Text, VStack } from '../../../components/primitives';
import { EnrichmentStageBreakdown } from '../../../components/workflow/EnrichmentStageBreakdown';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { EnrichmentStageReport } from '../../../hooks/useEnrichmentStream';
import { useResponsive } from '../../../hooks/useResponsive';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import type { WorkflowExecutionRecord } from '../../../lib/workflowsApi';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ExecutionStatus } from './ExecutionRow';

interface WorkflowExecutionDetailSheetProps {
  execution: WorkflowExecutionRecord | null;
  workflowName: string;
  visible: boolean;
  onClose: () => void;
}

const STAGE_NAMES = new Set([
  'profile',
  'posts',
  'comments',
  'analysis',
  'company',
  'company_posts',
]);
const STAGE_STATUSES = new Set(['success', 'failed', 'skipped', 'not_reached']);
const STAGE_REASONS = new Set([
  'provider_unavailable',
  'auth_required',
  'profile_not_found',
  'unknown',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(metadata: Record<string, unknown>, key: string): string | null {
  const value = metadata[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function readStages(metadata: Record<string, unknown>): EnrichmentStageReport[] {
  if (!Array.isArray(metadata.stages)) return [];

  return metadata.stages.flatMap((value) => {
    if (!isObject(value)) return [];
    const stage = value.stage;
    const status = value.status;
    const reasonCode = value.reasonCode;
    if (
      typeof stage !== 'string' ||
      !STAGE_NAMES.has(stage) ||
      typeof status !== 'string' ||
      !STAGE_STATUSES.has(status)
    ) {
      return [];
    }
    return [
      {
        stage: stage as EnrichmentStageReport['stage'],
        status: status as EnrichmentStageReport['status'],
        ...(typeof reasonCode === 'string' && STAGE_REASONS.has(reasonCode)
          ? { reasonCode: reasonCode as EnrichmentStageReport['reasonCode'] }
          : {}),
      },
    ];
  });
}

function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return '-';
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
}

function formatTimestamp(value: string | null, locale: Locale | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return format(date, 'MMM d, yyyy · HH:mm:ss', locale ? { locale } : undefined);
}

function SectionTitle({ index, children }: Readonly<{ index: string; children: ReactNode }>) {
  return (
    <HStack spacing="sm" align="center">
      <Text style={styles.sectionIndex}>{index}</Text>
      <Text variant="caption" color={colors.text.secondary} style={styles.sectionTitle}>
        {children}
      </Text>
      <View style={styles.sectionRule} />
    </HStack>
  );
}

function MetricCard({
  icon,
  value,
  label,
  tone,
}: Readonly<{ icon: ReactNode; value: number; label: string; tone: string }>) {
  return (
    <View style={[styles.metricCard, { borderTopColor: tone }]}>
      <HStack justify="between" align="center">
        <Text style={[styles.metricValue, { color: tone }]}>{value}</Text>
        {icon}
      </HStack>
      <Text variant="caption" color={colors.text.tertiary}>
        {label}
      </Text>
    </View>
  );
}

function DetailRow({
  label,
  value,
  icon,
  selectable,
}: Readonly<{ label: string; value: string; icon?: ReactNode; selectable?: boolean }>) {
  return (
    <View style={styles.detailRow}>
      <HStack spacing="sm" align="center" style={styles.detailLabel}>
        {icon}
        <Text variant="caption" color={colors.text.tertiary}>
          {label}
        </Text>
      </HStack>
      <Text
        selectable={selectable}
        variant="caption"
        color={colors.text.primary}
        style={styles.detailValue}
      >
        {value}
      </Text>
    </View>
  );
}

const STATUS_TONES: Record<ExecutionStatus, { color: string; bg: string; key: string }> = {
  success: {
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    key: 'common.statuses.success',
  },
  partial_success: {
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    key: 'common.statuses.partialSuccess',
  },
  failed: {
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    key: 'common.statuses.failed',
  },
  running: {
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    key: 'common.statuses.running',
  },
};

export function WorkflowExecutionDetailSheet({
  execution,
  workflowName,
  visible,
  onClose,
}: Readonly<WorkflowExecutionDetailSheetProps>) {
  const { t, language } = useTranslation();
  const { isMobile } = useResponsive();

  if (!execution) return null;

  const metadata = isObject(execution.metadata) ? execution.metadata : {};
  const stages = readStages(metadata);
  const context = [
    {
      key: 'platform',
      label: t('workflow.detail.platform'),
      value: readString(metadata, 'platform'),
    },
    {
      key: 'profileUrl',
      label: t('workflow.detail.profileUrl'),
      value: readString(metadata, 'profileUrl'),
    },
    { key: 'company', label: t('workflow.detail.company'), value: readString(metadata, 'company') },
    { key: 'leadId', label: t('workflow.detail.leadId'), value: readString(metadata, 'leadId') },
  ].filter((item): item is { key: string; label: string; value: string } => item.value !== null);
  const status = STATUS_TONES[execution.status];
  const locale = DATE_LOCALES[language];

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('workflow.detail.title')}
      position={isMobile ? 'bottom' : 'right'}
      size="lg"
    >
      <VStack spacing="xl">
        <View style={[styles.hero, { borderColor: status.color }]}>
          <Text variant="caption" color={colors.text.tertiary} style={styles.eyebrow}>
            {t('workflow.detail.outcome')}
          </Text>
          <Text variant="h4" color={colors.text.primary} style={styles.workflowName}>
            {workflowName}
          </Text>
          <View
            style={[styles.statusPill, { backgroundColor: status.bg, borderColor: status.color }]}
          >
            <View style={[styles.statusDot, { backgroundColor: status.color }]} />
            <Text style={[styles.statusText, { color: status.color }]}>{t(status.key)}</Text>
          </View>
        </View>

        <VStack spacing="md">
          <SectionTitle index="01">{t('workflow.detail.results')}</SectionTitle>
          <View style={styles.metricsGrid}>
            <MetricCard
              icon={<UserPlus size={18} color={colors.status.success.main} />}
              value={execution.leadsCreated}
              label={t('workflow.detail.leadsCreated')}
              tone={colors.status.success.main}
            />
            <MetricCard
              icon={<RefreshCw size={18} color={colors.primary.main} />}
              value={execution.leadsUpdated}
              label={t('workflow.detail.leadsUpdated')}
              tone={colors.primary.main}
            />
            <MetricCard
              icon={<AlertTriangle size={18} color={colors.status.error.main} />}
              value={execution.errorCount}
              label={t('workflow.detail.errors')}
              tone={execution.errorCount > 0 ? colors.status.error.main : colors.text.tertiary}
            />
          </View>
        </VStack>

        <VStack spacing="md">
          <SectionTitle index="02">{t('workflow.detail.timing')}</SectionTitle>
          <View style={styles.detailCard}>
            <DetailRow
              label={t('workflow.detail.started')}
              value={formatTimestamp(execution.startedAt, locale)}
              icon={<CalendarClock size={14} color={colors.text.tertiary} />}
            />
            <DetailRow
              label={t('workflow.detail.completed')}
              value={formatTimestamp(execution.completedAt, locale)}
              icon={<CheckCircle size={14} color={colors.text.tertiary} />}
            />
            <DetailRow
              label={t('workflow.detail.duration')}
              value={formatDuration(execution.durationMs)}
              icon={<Clock3 size={14} color={colors.text.tertiary} />}
            />
          </View>
        </VStack>

        {stages.length > 0 && (
          <VStack spacing="md">
            <SectionTitle index="03">{t('workflow.detail.stageProgress')}</SectionTitle>
            <EnrichmentStageBreakdown stages={stages} t={t} />
          </VStack>
        )}

        {context.length > 0 && (
          <VStack spacing="md">
            <SectionTitle index={stages.length > 0 ? '04' : '03'}>
              {t('workflow.detail.triggerContext')}
            </SectionTitle>
            <View style={styles.detailCard}>
              {context.map((item) => (
                <DetailRow
                  key={item.key}
                  label={item.label}
                  value={item.value}
                  selectable
                  icon={<Link2 size={14} color={colors.text.tertiary} />}
                />
              ))}
            </View>
          </VStack>
        )}

        <View style={styles.executionId}>
          <Text variant="caption" color={colors.text.tertiary}>
            {t('workflow.detail.executionId')}
          </Text>
          <Text selectable variant="caption" color={colors.text.secondary} style={styles.monoText}>
            {execution.id}
          </Text>
        </View>
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  hero: {
    position: 'relative',
    overflow: 'hidden',
    padding: tokens.spacing.lg,
    borderWidth: 1,
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.surface.cardOverlay,
    gap: tokens.spacing.xs,
  },
  eyebrow: { textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: '700' },
  workflowName: { fontWeight: '700', paddingRight: 130 },
  statusPill: {
    position: 'absolute',
    top: tokens.spacing.lg,
    right: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 999,
  },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusText: { fontSize: tokens.fontSize.xs, fontWeight: '700', textTransform: 'uppercase' },
  sectionIndex: { color: colors.primary.main, fontSize: 10, fontWeight: '800', letterSpacing: 0.8 },
  sectionTitle: { fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8 },
  sectionRule: { flex: 1, height: StyleSheet.hairlineWidth, backgroundColor: colors.border.subtle },
  metricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacing.sm },
  metricCard: {
    flex: 1,
    minWidth: 130,
    padding: tokens.spacing.md,
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.surface.row,
  },
  metricValue: { fontSize: 28, lineHeight: 32, fontWeight: '700', fontVariant: ['tabular-nums'] },
  detailCard: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.md,
    overflow: 'hidden',
    backgroundColor: colors.surface.row,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  detailLabel: { flex: 1, minWidth: 120 },
  detailValue: { flex: 1.5, textAlign: 'right', fontWeight: '500' },
  executionId: {
    gap: 4,
    paddingTop: tokens.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
  monoText: { fontFamily: 'monospace' },
});
